namespace distill.dashboards {
    export const FriendlyMetricFunctionNames: { [key: string]: string } = {
        'Avg': 'Average',
        'Sum': 'Total',
        'Count': 'Count',
        'CountDistinct': 'Distinct Count',
        'Min': 'Minimum',
        'Max': 'Maximum'
    };
    export const DashColorPalettes: { [key: string]: string[] } = {
        'Vibrant': [ '#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc', '#2ab7ca', '#fed766', '#4a4e4d' ],
        'Pastel': [ '#ffb3ba', '#ffdfba', '#ffffba', '#baffc9', '#bae1ff', '#e6e6fa', '#ffc3a0', '#ff677d', '#d4a5a5', '#392f5a', '#dcdcdc', '#a1e9c2' ],
        'Grayscale': [ '#212121', '#424242', '#616161', '#757575', '#9e9e9e', '#bdbdbd', '#e0e0e0', '#eeeeee', '#f5f5f5', '#37474f', '#455a64', '#546e7a' ],
        'Corporate': [ '#003366', '#004080', '#0059b3', '#0073e6', '#3385ff', '#6699ff', '#99ccff', '#cce6ff', '#607d8b', '#455a64', '#263238', '#1c2331' ],
        'Warm': [ '#ff4500', '#ff6347', '#ff7f50', '#cd5c5c', '#f08080', '#e9967a', '#fa8072', '#ffa07a', '#ff8c00', '#ffa500', '#ffd700', '#b8860b' ],
        'Cool': [ '#00ffff', '#00ced1', '#40e0d0', '#48d1cc', '#20b2aa', '#008b8b', '#008080', '#4682b4', '#5f9ea0', '#6495ed', '#00bfff', '#1e90ff' ],
        'Nature': [ '#556b2f', '#808000', '#6b8e23', '#228b22', '#2e8b57', '#3cb371', '#8fbc8f', '#daa520', '#cd853f', '#d2691e', '#8b4513', '#a0522d' ],
        'Neon': [ '#ff00ff', '#00ff00', '#00ffff', '#ffff00', '#ff0000', '#7fff00', '#ff1493', '#00fa9a', '#1e90ff', '#ff4500', '#9400d3', '#ffd700' ]
    };

    export function buildDatasetOption($option: JQuery, dataset: IDatasetMeta, isRestricted: boolean = false): void {
        $option.text(dataset.name);

        if (isRestricted) {
            // Restricted datasets have no origin the user can act on, so they get a lock glyph instead of
            // the origin icon the other options show
            $option.attr('data-icon-class', 'fa fa-lock');
        }
        else if (dataset.originIconUrl) {
            $option.attr('data-icon-url', dataset.originIconUrl);
            $option.attr('data-icon-tooltip', dataset.originDisplayName || dataset.origin || '');
        }

        if (!isRestricted && appn.utils.hasValue(dataset.cruxParentDatasetId))
            $option.attr('data-parent-value', dataset.cruxParentDatasetId!);

        // Flag the option for styling - restricted options use the standard text-danger class, with
        // .restricted-option as the hook the dashboard Design styles size the lock glyph through
        if (isRestricted)
            $option.attr('data-container-class', 'restricted-option text-danger');
    }

    export function getColumnOptionText(column: IDatasetColumnMeta, datasetAlias?: string | null): string {
        let result = column.displayName ?? '';

        if (column.designation != null && result.startsWith('_'))
            result = `${result.substring(1)}`;

        if (datasetAlias)
            result = `${datasetAlias}.${result}`;

        return result;
    }

    export function getDatasetRefsInDataQuery(dataset: IDataQuery | undefined): IDatasetReference[] {
        if (!dataset?.primaryDataset?.id)
            return [];

        const datasetRefs: IDatasetReference[] = [dataset.primaryDataset];
        if (dataset.links) {
            dataset.links.forEach(link => {
                if(link.linkedDataset?.id)
                    datasetRefs.push(link.linkedDataset);
            });
        }
        return datasetRefs;
    }

    export function getContextualColumnMeta(datasetIndex: number | undefined | null, columnId: string | undefined | null, datasetRefs: IDatasetReference[], datasets: IDatasetMeta[]) : { datasetRef: IDatasetReference | undefined, dataset: IDatasetMeta | undefined, columnMeta: IDatasetColumnMeta | undefined } {
        if (!appn.utils.hasValue(datasetIndex) || !columnId)
            return { datasetRef: undefined, dataset: undefined, columnMeta: undefined };

        const datasetRef = datasetRefs[datasetIndex!];
        if (!datasetRef)
            return { datasetRef: undefined, dataset: undefined, columnMeta: undefined };

        const dataset = datasets.find(t => t.id === datasetRef.id);
        if (!dataset)
            return { datasetRef: datasetRef, dataset: undefined, columnMeta: undefined };

        const columnMeta = dataset.columns.find(c => c.id === columnId);
        if (!columnMeta)
            return { datasetRef: datasetRef, dataset: dataset, columnMeta: undefined };

        return { datasetRef, dataset: dataset, columnMeta };
    }

    export function populatePrefixedColumnSelect($select: JQuery, datasetRefs: IDatasetReference[], columnTypeFilter: string[] | null, selectedValue?: string): IDatasetColumnMeta[] {
        const allDatasets = designer.getDatasets();
        const availableColumns: IDatasetColumnMeta[] = [];
        const showDatasetPrefix = datasetRefs.length > 1;

        $select.empty().append($('<option>').val('').text(T('Choose column...')));
        datasetRefs.forEach((datasetRef, index) => {
            const dataset = allDatasets.find(t => t.id === datasetRef.id);
            if (dataset) {
                const datasetPrefix = datasetRef.alias || dataset.name;
                dataset.columns.forEach(col => {
                    if (columnTypeFilter && !columnTypeFilter.includes(col.type))
                        return;

                    availableColumns.push(col);

                    const columnText = getColumnOptionText(col);
                    const optionText = showDatasetPrefix ? `${datasetPrefix}.${columnText}` : columnText;
                    const value = `${index}|${col.id}`;

                    $select.append($('<option>').val(value).text(optionText));
                });
            }
        });

        if (selectedValue)
            $select.val(selectedValue);

        return availableColumns;
    }

    /**
     * Column types a widget column may be bound to for a given global filter, mirroring the
     * server-side compatibility rules. Dropdown filters take the type of their source value
     * column; an unresolved source returns no types so the binding cannot be satisfied.
     */
    export function getGlobalFilterCompatibleColumnTypes(filter: IDashboardGlobalFilter, datasets: IDatasetMeta[]): string[] {
        if (filter.type !== DashboardGlobalFilterType.Dropdown)
            return GLOBAL_FILTER_COMPATIBLE_COLUMN_TYPES[filter.type] || [];

        if (!filter.sourceDatasetId || !filter.valueColumn)
            return [];

        const sourceDataset = (datasets || []).find(d => d.id === filter.sourceDatasetId);
        const valueColumn = sourceDataset?.columns?.find(c => c.id === filter.valueColumn);
        return valueColumn ? [valueColumn.type] : [];
    }

    export function getHeaderName(colIdentifier: IDataQueryColumnIdentifier, component: IComponent): string {
        if (!colIdentifier || !appn.utils.hasValue(colIdentifier.datasetIndex) || !colIdentifier.columnId)
            return appn.emptyString;

        if (colIdentifier.alias)
            return colIdentifier.alias;

        const allDatasets = designer.getDatasets();
        const datasetRefs = getDatasetRefsInComponent(component);
        const datasetRef = datasetRefs[colIdentifier.datasetIndex!];
        if (!datasetRef)
            return appn.emptyString;

        // Restricted datasets live in their own list (carrying read-only column metadata for the renderers)
        const dataset = allDatasets.find(t => t.id === datasetRef.id)
            || designer.getRestrictedDatasets().find(t => t.id === datasetRef.id);
        if (!dataset)
            return appn.emptyString;

        const columnMeta = dataset.columns.find(c => c.id === colIdentifier.columnId);
        if (!columnMeta)
            return appn.emptyString;

        return columnMeta.displayName;
    }

    export function getDatasetRefsInComponent(component: IComponent): IDatasetReference[] {
        const datasets: IDatasetReference[] = [];
        const definition = componentManager.getDefinition(component.type);
        if (!definition)
            return datasets;

        const dataQueryPropNames = componentManager.getDataQueryPropertyNames(definition);
        for (const dataQueryPropName of dataQueryPropNames) {
            const dataQuery = (component as any)[dataQueryPropName] as IDataQuery | undefined;
            if (!dataQuery?.primaryDataset?.id)
                continue;

            datasets.push(dataQuery.primaryDataset);
            if (dataQuery.links) {
                dataQuery.links.forEach(link => {
                    if (link.linkedDataset?.id)
                        datasets.push(link.linkedDataset);
                });
            }
        }

        return datasets;
    }

    function getFinalMetricAlias(metric: IDataQueryMetric, metricIndex: number, aggregation: IDataQueryAggregation | null, datasetRefs: IDatasetReference[], datasets: IDatasetMeta[]) : string {
        const competingAliases = getCompetingAggregationAliases(aggregation, 'metric', metricIndex);

        let finalAlias: string;
        if (metric.alias) {
            finalAlias = metric.alias;
            let counter = 1;
            while (competingAliases.has(finalAlias))
                finalAlias = `${metric.alias}_${counter++}`;
        } else {
            const funcName = FriendlyMetricFunctionNames[metric.function] || metric.function;
            let defaultAlias: string;
            if (metric.function === 'Count' || !metric.columnId)
                defaultAlias = `${funcName}_${metricIndex + 1}`;
            else {
                const { columnMeta } = getContextualColumnMeta(metric.datasetIndex, metric.columnId, datasetRefs, datasets);
                const colDisplay = columnMeta ? getColumnOptionText(columnMeta) : 'Unknown';
                defaultAlias = `${funcName} ${colDisplay}`;
            }

            finalAlias = defaultAlias;
            let counter = 1;
            while (competingAliases.has(finalAlias))
                finalAlias = `${defaultAlias}_${counter++}`;
        }

        return finalAlias;
    }

    export function getExpectedMetricHeader(component: IComponent, dataQuery: IDataQuery, metric: IDataQueryMetric, index: number) : string {
        const aggregation = dataQuery.aggregation;
        const datasetRefs = getDatasetRefsInComponent(component);
        // Restricted datasets carry read-only column metadata - the renderers need it for header prediction
        const datasets = designer.getDatasets().concat(designer.getRestrictedDatasets());

        return getFinalMetricAlias(metric, index, aggregation, datasetRefs, datasets);
    }

    export function getMetricDisplayText(metric: IDataQueryMetric, metricIndex: number, aggregation: IDataQueryAggregation | null, datasetRefs: IDatasetReference[], datasets: IDatasetMeta[]): string {
        const finalAlias = getFinalMetricAlias(metric, metricIndex, aggregation, datasetRefs, datasets);

        if (metric.function !== 'Count' && metric.columnId) {
            const { datasetRef, dataset, columnMeta } = getContextualColumnMeta(metric.datasetIndex, metric.columnId, datasetRefs, datasets);
            if (datasetRef && dataset && columnMeta)
                return `${finalAlias} (${datasetRef.alias || dataset.name})`;
        }

        return finalAlias;
    }

    function getCompetingAggregationAliases(aggregation: IDataQueryAggregation | null, sourceObjectType: 'groupBy' | 'breakDownBy' | 'metric', sourceMetricIndex: number = -1): Set<string> {
        const competingAliases = new Set<string>();
        if (!aggregation)
            return competingAliases;

        const { groupBy, breakDownBy, metrics } = aggregation;

        if (sourceObjectType !== 'groupBy' && groupBy && groupBy.alias)
            competingAliases.add(groupBy.alias);

        if (sourceObjectType !== 'breakDownBy' && breakDownBy && breakDownBy.alias)
            competingAliases.add(breakDownBy.alias);

        if (metrics) {
            metrics.forEach((m, index) => {
                if (sourceObjectType !== 'metric' || index !== sourceMetricIndex) {
                    if (m.alias)
                        competingAliases.add(m.alias);
                }
            });
        }

        return competingAliases;
    }

    export function runAggregationAliasDeduplication(dataQuery: IDataQuery, newAlias: string, sourceIdentifier: string, sourceMetricIndex: number = -1): string {
        if (!newAlias)
            return "";

        let sourceObjectType: 'groupBy' | 'breakDownBy' | 'metric' | null = null;
        if (sourceIdentifier === 'metric')
            sourceObjectType = 'metric';
        else if (sourceIdentifier?.endsWith('groupBy'))
            sourceObjectType = 'groupBy';
        else if (sourceIdentifier?.endsWith('breakDownBy'))
            sourceObjectType = 'breakDownBy';
        else {
            console.warn("runAggregationAliasDeduplication called with unknown sourceIdentifier:", sourceIdentifier);
            return newAlias;
        }
        
        if (!dataQuery || !dataQuery.aggregation) 
            return newAlias;
        
        const competingAliases = getCompetingAggregationAliases(dataQuery.aggregation, sourceObjectType, sourceMetricIndex);

        let finalAlias = newAlias;
        let counter = 1;
        while (competingAliases.has(finalAlias))
            finalAlias = `${newAlias}_${counter++}`;

        return finalAlias;
    }

    export function formatDateByGrain(isoDateString: string, grain: string | undefined | null): string {
        if (!isoDateString)
            return '';
        
        if (!grain || grain === 'None')
            return isoDateString;
        
        const date = new Date(isoDateString);
        if (isNaN(date.getTime()))
            return isoDateString;

        const pad = (n: number) => n < 10 ? '0' + n : n;
        const year = date.getUTCFullYear();
        const month = date.getUTCMonth() + 1;
        const day = date.getUTCDate();
        const hours = date.getUTCHours();
        const minutes = date.getUTCMinutes();
        const seconds = date.getUTCSeconds();
        switch (grain) {
            case 'Year':
                return `${year}`;
            case 'Quarter':
                const q = Math.floor((month - 1) / 3) + 1;
                return `${year} Q${q}`;
            case 'Month':
                return `${year}-${pad(month)}`;
            case 'Week':
                // ISO-8601 week: shifting d to the week's Thursday, then taking the label's
                // year from d (the ISO week-year), so boundary weeks label correctly
                // (2024-12-30 is 2025 W01, not 2024 W01).
                const d = new Date(Date.UTC(year, month - 1, day));
                const dayNum = d.getUTCDay() || 7;
                d.setUTCDate(d.getUTCDate() + 4 - dayNum);
                const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
                const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);

                return `${d.getUTCFullYear()} W${pad(weekNo)}`;
            case 'Day':
                return `${year}-${pad(month)}-${pad(day)}`;
            case 'Hour':
            case 'Minute':
                return `${year}-${pad(month)}-${pad(day)} ${pad(hours)}:${pad(minutes)}`;
            case 'Second':
            default:
                return `${year}-${pad(month)}-${pad(day)} ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
        }
    }

    export function getIntervalMs(interval: string): number {
        switch (interval) {
            case '30s': return 30 * 1000;
            case '1m': return 60 * 1000;
            case '5m': return 5 * 60 * 1000;
            case '15m': return 15 * 60 * 1000;
            case '1h': return 60 * 60 * 1000;
            default: return 5 * 60 * 1000;
        }
    }
    
    export function hexToRgba(hex: string, opacityPercent: number): string {
        // Handle transparent explicit or empty
        if (!hex || hex === 'transparent')
            return 'rgba(0,0,0,0)';

        // Strip hash
        hex = hex.replace('#', '');

        // Handle shorthand #CCC -> #CCCCCC
        if (hex.length === 3)
            hex = hex.split('').map(c => c + c).join('');

        const r = parseInt(hex.substring(0, 2), 16) || 0;
        const g = parseInt(hex.substring(2, 4), 16) || 0;
        const b = parseInt(hex.substring(4, 6), 16) || 0;

        // Convert 0-100 to 0.0-1.0
        const alpha = Math.max(0, Math.min(100, opacityPercent)) / 100;

        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    export let exportUrl: string = '';
    export let isPublicView: boolean = false;

    export function downloadBlob(blob: Blob, fileName: string): void {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        }, 100);
    }

    export function getExportFileName(component: IDashboardComponent, extension: 'csv' | 'xlsx'): string {
        const title = component.label?.eng || component.title?.eng || 'Table_Export';
        const safeTitle = title.trim().replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/^_+|_+$/g, '');
        const now = new Date();
        const pad = (n: number) => n < 10 ? '0' + n : String(n);
        const dateStr = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
        return `${safeTitle || 'Table_Export'}_${dateStr}.${extension}`;
    }

    export async function exportTable(args: ITableExportArgs): Promise<void> {
        try {
            const requestUrl = args.url || exportUrl;
            const token = args.accessToken || (distill.dashboards.publicView?.getAccessToken ? distill.dashboards.publicView.getAccessToken() : null) || '';
            // The server rebuilds the view from the design (component + filters) and writes the file in the
            // requested format — the client only identifies WHAT to export, never ships the data itself.
            const response = await fetch(requestUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    accessToken: token,
                    fileName: args.fileName,
                    sheetName: args.sheetName,
                    componentId: args.componentId,
                    filters: args.filters || [],
                    sortOverrides: args.sortOverrides || null
                })
            });

            if (!response.ok) {
                const errorText = await response.text().catch(() => '');
                console.error(`Export request failed (${response.status} ${response.statusText}):`, errorText);
                throw new Error(`Export failed: ${response.status} ${response.statusText}`);
            }

            const blob = await response.blob();
            downloadBlob(blob, args.fileName);
            appn.toast.notify(appn.toast.Type.success, T(`Export Complete: {0}`, args.fileName));
        } catch (err) {
            console.error('Export error:', err);
            appn.toast.notify(appn.toast.Type.error, T('Failed to export table data.'));
        }
    }
}
